import { mutate, type Ack } from './api'
import { buzz } from './haptic'
import { reducedMotion } from './motion'
import { appendNote } from './rebook-message'
import type { Lead } from './types'
import { el, shortDate, todayISO, noteDate } from './ui'
import { offerUndo, undoPatch } from './undo'
import './let-go.css'

// Swiping an enquiry away (Liam, 10 Oct 2026: "on the new enquiries id like to be able to swipe to delete them ... it
// maybe be spam or not the correct area or the are not interested and a satisfying swipe could be a good addition").
// On a phone, on New jobs and Messages: slide an enquiry left and "Remove" shows behind it; past halfway the word
// becomes "Let go" and the phone buzzes; let go there and the card stays open on three reasons. A reason folds the card
// away and offers Undo; a tap anywhere else slides it back with nothing saved. A slide more up and down than across is a
// scroll, left alone. The same reasons sit behind "Remove" on the enquiry's own page (the computer, and anyone who
// would rather tap).
//   - Spam: off every list (kept, marked removed; History has it). POST /api/leads/:id/remove.
//   - Not our area / Not interested: Not going ahead, with the reason in What's happened.

export type LetGoReason = 'spam' | 'area' | 'interested'
export const LET_GO_REASONS: [LetGoReason, string][] = [['spam', 'Spam'], ['area', 'Not our area'], ['interested', 'Not interested']]
const NOTE: Record<Exclude<LetGoReason, 'spam'>, string> = { area: 'let go, not our area', interested: 'let go, not interested' }

// Enquiries let go whose save is on its way: both screens leave them out, so a redraw never brings back a card that
// just folded away. Cleared on every answer (saved, refused, or held for signal): Roundly shows what is stored, so a
// change still waiting for signal brings the card back with the queue's waiting mark until it lands.
const leaving = new Set<number>()
export const isLeaving = (id: number): boolean => leaving.has(id)

// The save, with Undo. after(): reload the screen once the save has answered, whatever the answer. Read from the
// answer mutate returns: its onAck runs only on success (src/api.ts), so a refusal is said here, with its reason.
export function letGoEnquiry(l: Lead, reason: LetGoReason, after: () => void): void {
  const path = `/api/leads/${l.id}`
  leaving.add(l.id)
  const refused = (ack: Ack, words: string): void => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? words)
  }
  if (reason === 'spam') {
    void mutate({ label: `Removed as spam: ${l.name}`, method: 'POST', path: `${path}/remove`, body: { version: l.version, removed: true } }).then((ack) => {
      leaving.delete(l.id)
      refused(ack, "It couldn't be removed.")
      after()
      if (!ack.ok) return
      const row = ack.row as { version?: number } | undefined
      const version = typeof row?.version === 'number' ? row.version : l.version + 1
      // A WhatsApp enquiry's number is remembered (0081, item 8): its WhatsApps stay on the phone from now on; Undo forgets it.
      offerUndo(byWhatsApp(l) ? `${l.name} removed as spam. Their WhatsApps stay on your phone from now on.` : `${l.name} removed as spam`, () => {
        void mutate({ label: `Put back: ${l.name}`, method: 'POST', path: `${path}/remove`, body: { version, removed: false } }).then((back) => {
          if (!back.ok && back.status >= 400 && back.status < 500) window.alert(`Undo was refused: ${back.error ?? 'rejected'}`)
          after()
        })
      })
    })
    return
  }
  const note = appendNote(l.contact_note, `${noteDate(todayISO())}: ${NOTE[reason]}`)
  const body: Record<string, unknown> = { version: l.version, status_raw: 'DECLINED', let_go: reason }
  // Left out when the line would not fit: the owner's own note must survive.
  if (note !== (l.contact_note ?? '')) body.contact_note = note
  void mutate({ label: `Not going ahead: ${l.name}`, method: 'PATCH', path, body }).then((ack) => {
    leaving.delete(l.id)
    refused(ack, "It couldn't be saved.")
    after()
    // Offered for a save made or waiting for signal; never for a refused one (undoPatch).
    undoPatch({
      text: `${l.name}: ${reason === 'area' ? 'not our area' : 'not interested'}`,
      label: `Undo not going ahead: ${l.name}`,
      path,
      ack,
      version: l.version,
      body: { status_raw: l.status_raw ?? null, contact_note: l.contact_note ?? null },
      after,
    })
  })
}

// An enquiry that came by WhatsApp (its first words are kept as "WhatsApp message…", functions/lib/wa-inbox.ts and
// inbound-text.ts): Spam also covers someone who isn't a customer (the round's own team messaging the work number).
export const byWhatsApp = (l: Pick<Lead, 'initial_lead'>): boolean => /^WhatsApp message\b/.test(String(l.initial_lead ?? ''))

// The three reasons as buttons, for behind a slid card or under "Remove" on the page. whatsapp: on the enquiry's page, Spam
// says what it does to their WhatsApps (picture 9 at https://claude.ai/artifact/PdC9wPAwE4rkQepWP8LXFz).
export function reasonButtons(name: string, pick: (reason: LetGoReason, button: HTMLButtonElement) => false | void, opts: { whatsapp?: boolean } = {}): HTMLElement {
  const box = el('div', { class: 'letgo-reasons', role: 'group', 'aria-label': `Remove ${name}: why?` })
  // One pick only: a second tap while the card folds away would save twice (peer review).
  let picked = false
  for (const [reason, words] of LET_GO_REASONS) {
    const b = el('button', { type: 'button', class: `letgo-reason letgo-reason-${reason}`, 'aria-label': `${words}: ${name}` }, words) as HTMLButtonElement
    if (reason === 'spam' && opts.whatsapp) {
      b.textContent = 'Spam or not a customer'
      b.append(el('span', { class: 'letgo-reason-sub' }, 'Their WhatsApps stay on your phone from now on. Undo puts them back.'))
    }
    b.addEventListener('click', (e) => {
      e.stopPropagation()
      if (picked) return
      picked = true
      for (const other of box.querySelectorAll<HTMLButtonElement>('button')) other.disabled = true
      // A pick the screen turned down (the job is still saving) leaves the three reasons ready for another tap.
      if (pick(reason, b) === false) {
        picked = false
        for (const other of box.querySelectorAll<HTMLButtonElement>('button')) other.disabled = false
      }
    })
    box.append(b)
  }
  return box
}

// The card folds away, then the save runs (at once with reduced motion).
function foldAway(row: HTMLElement, under: HTMLElement | null, then: () => void, side: 1 | -1 = -1): void {
  if (reducedMotion() || typeof row.animate !== 'function') { under?.remove(); then(); return }
  const h = row.offsetHeight
  row.style.overflow = 'hidden'
  const a = row.animate([
    { transform: row.style.transform || 'translateX(0)', opacity: 1, height: `${h}px` },
    { transform: `translateX(${side * 105}%)`, opacity: 0, height: `${h}px`, offset: 0.55 },
    { transform: `translateX(${side * 105}%)`, opacity: 0, height: '0px', marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px' },
  ], { duration: 380, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' })
  under?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, fill: 'forwards' })
  let done = false
  const finish = (): void => { if (done) return; done = true; under?.remove(); then() }
  a.onfinish = finish
  a.oncancel = finish
  window.setTimeout(finish, 600)
}

function underFor(row: HTMLElement, name: string, pick: (reason: LetGoReason, button: HTMLButtonElement) => void, side: 1 | -1 = -1, rightWord = 'Next week'): HTMLElement | null {
  const list = row.parentElement
  if (!list) return null
  if (getComputedStyle(list).position === 'static') list.style.position = 'relative'
  const under = el('div', { class: `letgo-under${side > 0 ? ' letgo-right' : ''}` })
  under.style.top = `${row.offsetTop}px`
  under.style.left = `${row.offsetLeft}px`
  under.style.width = `${row.offsetWidth}px`
  under.style.height = `${row.offsetHeight}px`
  // Liam, 2 Nov 2026: red with a bin to the left; soft lemon "Next week" with a calendar to the right (option 2).
  if (side > 0) under.append(el('b', { class: 'letgo-word', 'aria-hidden': 'true' }, el('span', { class: 'letgo-icon letgo-icon-week' }), rightWord))
  else under.append(el('b', { class: 'letgo-word', 'aria-hidden': 'true' }, el('span', { class: 'letgo-icon letgo-icon-bin' }), 'Bin'), reasonButtons(name, pick))
  list.insertBefore(under, row)
  return under
}

// Slide to let go, on one enquiry's card or row on a phone. busy(): a save in flight on it, so it does not slide.
// opts.left: a slide left past halfway does this at once (a customer: take them off), instead of the three reasons.
// opts.right: a slide right past halfway does this (put them back a week). Without it a slide right is left alone.
export function slideToLetGo(row: HTMLElement, name: string, choose: (reason: LetGoReason) => void, busy: () => boolean = () => false,
  opts: { left?: () => void; right?: { act: () => void; word?: string } } = {}): void {
  row.classList.add('letgo-row')
  let start: { x: number; y: number; id: number } | null = null
  let dragging = false
  let armed = false
  let open = false
  let side: 1 | -1 = -1
  let under: HTMLElement | null = null
  const move = (px: number, animate: boolean): void => {
    row.style.transition = animate ? 'transform 260ms cubic-bezier(.2,.9,.25,1.2)' : ''
    row.style.transform = px ? `translateX(${px}px)` : ''
  }
  const reasonsWidth = (): number => Math.min(row.offsetWidth * 0.82, 300)
  // A tap anywhere but the reasons only slides the card back: the tap itself does nothing else (not even open the card).
  // A card redrawn meanwhile has gone, so the tap is left alone.
  const outside = (e: Event): void => {
    if (!row.isConnected) { document.removeEventListener('pointerdown', outside, true); return }
    if (under && under.contains(e.target as Node)) return
    e.stopPropagation()
    e.preventDefault()
    const swallow = (c: Event): void => { c.stopPropagation(); c.preventDefault() }
    document.addEventListener('click', swallow, { capture: true, once: true })
    window.setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 400)
    close()
  }
  const close = (): void => {
    open = false
    document.removeEventListener('pointerdown', outside, true)
    under?.classList.remove('letgo-open')
    move(0, true)
    const gone = under
    under = null
    window.setTimeout(() => { gone?.remove(); row.classList.remove('letgo-sliding') }, 280)
  }
  const pick = (reason: LetGoReason): void => {
    document.removeEventListener('pointerdown', outside, true)
    open = false
    buzz('success')
    row.inert = true
    foldAway(row, under, () => choose(reason))
    under = null
  }
  row.addEventListener('pointerdown', (e) => {
    if (open || e.button !== 0 || busy() || (e.target as Element).closest('a, input, select, textarea, [role="menu"], [role="menuitem"]')) return
    start = { x: e.clientX, y: e.clientY, id: e.pointerId }
    dragging = false
    armed = false
  })
  row.addEventListener('pointermove', (e) => {
    if (!start || e.pointerId !== start.id) return
    const dy = e.clientY - start.y
    if (!dragging) {
      const across = e.clientX - start.x
      const sideways = Math.abs(across) > Math.abs(dy) * 1.3
      if (sideways && (across < -10 || across > 10 && opts.right)) {
        dragging = true
        side = across < 0 ? -1 : 1
        try { row.setPointerCapture(e.pointerId) } catch { /* the slide still follows */ }
        under?.remove()
        under = underFor(row, name, (reason) => pick(reason), side, opts.right?.word)
        row.classList.add('letgo-sliding', 'letgo-dragging')
      } else if (Math.abs(dy) > 10 || across > 10) { start = null; return } else return
    }
    const dx = side < 0 ? Math.min(0, e.clientX - start.x) : Math.max(0, e.clientX - start.x)
    const w = row.offsetWidth || 1, t = w * 0.45, d = Math.abs(dx)
    // Past halfway the card gives less, so it feels held and you know you can let go.
    move(side * Math.min(d <= t ? d : t + (d - t) * 0.45, w * 0.92), false)
    under?.style.setProperty?.('--letgo-p', String(Math.min(1, 0.35 + (d / t) * 0.65)))
    const now = d > t
    if (now !== armed) {
      armed = now
      under?.classList.toggle('letgo-armed', armed)
      under?.classList.remove('letgo-snap')
      if (armed) { void under?.offsetWidth; under?.classList.add('letgo-snap'); buzz('medium') }
    }
  })
  const end = (cancelled: boolean): void => {
    if (!start) return
    const was = dragging
    start = null
    dragging = false
    if (!was) return
    // A slide is not a tap: the card's own click (open it) must not follow it.
    const swallow = (e: Event): void => { e.stopPropagation(); e.preventDefault() }
    row.addEventListener('click', swallow, { capture: true, once: true })
    window.setTimeout(() => row.removeEventListener('click', swallow, { capture: true }), 400)
    row.classList.remove('letgo-dragging')
    // Straight through: a slide right (a week), or left on a customer (taken off). The card folds away that side.
    const act = side > 0 ? opts.right?.act : opts.left
    if (armed && !cancelled && under && act) {
      armed = false
      buzz('success')
      row.inert = true
      foldAway(row, under, act, side)
      under = null
      return
    }
    if (armed && !cancelled && under) {
      armed = false
      open = true
      under.classList.remove('letgo-armed')
      under.classList.add('letgo-open')
      move(-reasonsWidth(), true)
      under.querySelector<HTMLButtonElement>('.letgo-reason')?.focus({ preventScroll: true })
      window.setTimeout(() => { if (open) document.addEventListener('pointerdown', outside, true) }, 0)
      return
    }
    armed = false
    close()
  }
  row.addEventListener('pointerup', () => end(false))
  row.addEventListener('pointercancel', () => end(true))
}


// Legacy app removals remain in Old customers; plain imported closed rows have always lived in New jobs.
export function inOldEnquiries(l: Lead): boolean {
  if (l.converted_customer_id) return false
  if (l.deleted_at || l.enquiry_archive === 'old') return true
  return l.enquiry_archive !== 'messages' && !!(l.let_go_reason || ['declined', 'cancelled'].includes(l.status) && /: let go, (?:not our area|not interested)(?:\n|$)/i.test(l.contact_note ?? ''))
}

export function didntGoAhead(l: Lead): boolean {
  return !l.converted_customer_id && !l.details_binned_at && !inOldEnquiries(l) &&
    (l.status === 'declined' || l.status === 'cancelled' || /\bloss\b/i.test(l.status_raw ?? ''))
}

export function didntGoAheadWords(l: Lead): string {
  const reason = l.let_go_reason || (/let go, not our area\s*$/im.test(l.contact_note ?? '') ? 'Not our area' : /let go, not interested\s*$/im.test(l.contact_note ?? '') ? 'Not interested' : l.status === 'cancelled' ? 'Cancelled' : 'Not going ahead')
  const on = l.let_go_on || (l.enquiry_archive === 'messages' ? l.updated_at : null)
  return `${reason} · ${on ? shortDate(on.slice(0, 10)) : 'Date not recorded'}`
}

export async function moveClosedEnquiry(l: Lead, action: 'old' | 'bring-back', after: () => void): Promise<void> {
  const path = `/api/leads/${l.id}/archive`
  let queuedKey = ''
  const ack = await mutate({ label: action === 'old' ? `Move ${l.name} to Old customers` : `Bring back ${l.name}`, method: 'POST', path,
    body: { version: l.version, action }, onQueued: m => { queuedKey = m.key } })
  after()
  if (!ack.ok && ack.status !== 0) { if (ack.status >= 400) window.alert(ack.error ?? 'It could not be saved.'); return }
  const row = ack.row as (Lead & { archive_key: string }) | undefined
  const version = row?.version ?? l.version + 1
  const undoKey = row?.archive_key ?? queuedKey
  offerUndo(action === 'old' ? `${l.name} moved to Old customers, under Enquiries` : `${l.name} is back in active enquiries`, () => {
    void mutate({ label: `Undo move: ${l.name}`, method: 'POST', path, body: { version, action: 'undo', undo: undoKey } }).then(back => {
      if (!back.ok && back.status >= 400) window.alert(back.error ?? 'Undo could not be saved.')
      after()
    })
  })
}
